import { useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { motion } from "framer-motion";
import { format, startOfMonth, addMonths } from "date-fns";
import {
  ArrowLeft,
  Zap,
  LogOut,
  Edit2,
  Check,
  X,
  Lock,
  ChevronRight,
  KeyRound,
  Bell,
  UserMinus,
  CheckCircle2,
  ArrowRight,
  Mail,
  CreditCard,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { isPaidPlan, isTrialExhausted, trialEntriesRemaining } from "@/lib/trial";
import { getCurrentPlanId, PLANS, unfoldingsLabel, type PlanId } from "@/lib/billing";
import { CancelPlanDialog } from "@/components/cancel-plan-dialog";
import { SwitchPlanDialog } from "@/components/switch-plan-dialog";
import { DeleteAccountDialog } from "@/components/delete-account-dialog";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { cancelScheduledChange, openBillingPortal } from "@/lib/billing-api";
import { requestPasswordReset } from "@/lib/auth-api";
import { CalendarFlowProvider } from "@/components/calendar/calendar-flow";
import { CalendarSettingsSection } from "@/components/calendar/settings-calendar-section";
import { AiAssistantsSection } from "@/components/assistant/ai-assistants-section";

function friendlyDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

/** Eyebrow label that opens each section (brand style guide). */
function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="eyebrow mb-3 px-1">{children}</h2>;
}

function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={cn("surface-card", className)}>{children}</div>;
}

export default function SettingsScreen() {
  const [, setLocation] = useLocation();
  const { user, logout, refetch } = useAuth();

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(
    user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "",
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalValue, setGoalValue] = useState(user?.journalingGoal || "");
  const [goalError, setGoalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [switchTarget, setSwitchTarget] = useState<PlanId | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const [passwordState, setPasswordState] = useState<"idle" | "sending" | "sent" | "no_password" | "error">("idle");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const displayName = user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "You";

  /** Shared PATCH for the two inline editors. Returns an error string, or null
   * on success — a failed save must keep the editor open with what was typed,
   * never close and silently revert on the next refetch. */
  async function saveProfile(patch: Record<string, string>): Promise<string | null> {
    try {
      const res = await authFetch(apiUrl("/user/profile"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        return body.error ?? `Couldn't save your changes (${res.status}).`;
      }
      return null;
    } catch {
      return "Couldn't reach the server. Check your connection and try again.";
    }
  }

  async function saveName() {
    setSaving(true);
    setNameError(null);
    const error = await saveProfile({ displayName: nameValue.trim() });
    setSaving(false);
    if (error) {
      setNameError(error);
      return;
    }
    setEditingName(false);
    refetch();
  }

  async function saveGoal() {
    setSaving(true);
    setGoalError(null);
    const error = await saveProfile({ journalingGoal: goalValue.trim() });
    setSaving(false);
    if (error) {
      setGoalError(error);
      return;
    }
    setEditingGoal(false);
    refetch();
  }

  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const isSubscriber = isPaidPlan(user?.plan);
  const remaining = trialEntriesRemaining(user);
  const exhausted = isTrialExhausted(user);
  const used = user?.trialEntriesUsed ?? 0;
  const limit = user?.trialEntriesLimit ?? 5;
  // Paid plans now carry a monthly allowance too — only legacy plans report null.
  const hasLimit = user?.trialEntriesLimit != null;
  const progressPct = hasLimit ? Math.min(100, (used / Math.max(1, limit)) * 100) : 100;
  const currentPlan = PLANS.find((p) => p.id === getCurrentPlanId(user)) ?? PLANS[0];

  // Quota resets on the 1st of the next calendar month (see backend trial.ts) —
  // independent of the Stripe billing/renewal date, which can fall any day.
  const now = new Date();
  const resetsOn = format(startOfMonth(addMonths(now, 1)), "MMM d");
  const dayOfMonth = now.getDate();
  const dailyPace = used > 0 ? used / dayOfMonth : 0;
  const paceLabel =
    hasLimit && !exhausted && dailyPace > 0
      ? `about ${dailyPace < 1 ? dailyPace.toFixed(1) : Math.round(dailyPace)} a day at your current pace`
      : null;

  const planCancelsAt = friendlyDate(user?.planCancelsAt);
  const pendingPlanId = (user?.pendingPlan as PlanId | null | undefined) ?? null;
  const pendingPlanName = pendingPlanId ? PLANS.find((p) => p.id === pendingPlanId)?.name ?? null : null;
  const pendingEffectiveFriendly = friendlyDate(user?.pendingPlanEffectiveAt);

  // A Stripe subscription behind the plan (either Stripe account) — cancel, card and
  // invoices live in the Stripe Customer Portal for that account.
  const hasBillingPortal = Boolean(user?.billingAccount);
  const paymentFailing = user?.subscriptionStatus === "past_due";

  async function handleManageBilling() {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      window.location.assign(await openBillingPortal());
    } catch (err) {
      setPortalError((err as Error).message ?? "Couldn't open billing. Try again in a moment.");
      setOpeningPortal(false);
    }
  }

  // An existing Plus subscriber switches tiers via the scheduled-switch flow, never
  // a new checkout — a second subscription would double-bill. This is the only
  // route to Premium for them.
  const canSwitchToPremium =
    isSubscriber && currentPlan.id === "plus" && !planCancelsAt && pendingPlanId !== "premium";

  async function handleUndoSwitch() {
    setUndoing(true);
    setUndoError(null);
    try {
      await cancelScheduledChange();
      refetch({ silent: true });
    } catch (err) {
      setUndoError((err as Error).message ?? "Couldn't undo. Try again in a moment.");
    } finally {
      setUndoing(false);
    }
  }

  async function handleChangePassword() {
    if (!user?.email || passwordState === "sending") return;
    setPasswordState("sending");
    setPasswordError(null);
    const result = await requestPasswordReset(user.email);
    if (result.status === "error") {
      setPasswordState("error");
      setPasswordError(result.error);
    } else if (result.status === "no_password") {
      setPasswordState("no_password");
    } else {
      setPasswordState("sent");
    }
  }

  return (
    <Layout>
      {/* Single column at every width: the old two-column grid reflowed into
          "deactivate → billing → privacy" on phones, which put a destructive
          action above marketing. */}
      <div className="mx-auto w-full max-w-3xl px-5 py-8">
        <button
          onClick={() => setLocation("/unfoldings")}
          className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Your Unfoldings
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <header className="mb-8">
            <h1 className="page-title font-display text-3xl">Settings</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage your account and preferences</p>
          </header>

          <div className="space-y-8">
            {/* ── Profile ─────────────────────────────────────────────── */}
            <section>
              <SectionHeading>Profile</SectionHeading>
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  {user?.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={displayName}
                      className="h-16 w-16 shrink-0 rounded-full ring-2 ring-primary/20"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-lilac font-display text-2xl text-primary ring-2 ring-primary/20">
                      {initials}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    {editingName ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <input
                            autoFocus
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveName();
                              if (e.key === "Escape") {
                                setEditingName(false);
                                setNameError(null);
                              }
                            }}
                            maxLength={80}
                            aria-label="Your name"
                            className="min-w-0 flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                          <button
                            onClick={saveName}
                            disabled={saving}
                            aria-label="Save name"
                            className="text-green-600 transition-colors hover:text-green-700 disabled:opacity-50"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingName(false);
                              setNameError(null);
                            }}
                            aria-label="Cancel"
                            className="text-stone-400 transition-colors hover:text-stone-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        {nameError && <p className="mt-2 text-xs text-destructive">{nameError}</p>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {/* break-words, not truncate: a long name should wrap on a
                            phone rather than be cut off. */}
                        <h3 className="min-w-0 break-words font-display text-2xl font-medium tracking-[-0.015em] text-foreground">
                          {displayName}
                        </h3>
                        <button
                          onClick={() => setEditingName(true)}
                          aria-label="Edit name"
                          className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {user?.email && (
                      <p className="mt-0.5 break-all text-sm text-muted-foreground">{user.email}</p>
                    )}
                  </div>
                </div>
              </Card>
            </section>

            {/* ── Journaling ──────────────────────────────────────────── */}
            <section>
              <SectionHeading>Journaling</SectionHeading>
              <Card className="overflow-hidden">
                <div className="p-6">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">My journaling intention</p>
                    {!editingGoal && (
                      <button
                        onClick={() => setEditingGoal(true)}
                        className="shrink-0 text-xs font-medium text-primary transition-colors hover:text-primary/80"
                      >
                        {user?.journalingGoal ? "Edit" : "Set intention"}
                      </button>
                    )}
                  </div>
                  {editingGoal ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={goalValue}
                          onChange={(e) => setGoalValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveGoal();
                            if (e.key === "Escape") {
                              setEditingGoal(false);
                              setGoalError(null);
                            }
                          }}
                          maxLength={120}
                          placeholder="e.g. Process what I'm learning"
                          aria-label="Journaling intention"
                          className="min-w-0 flex-1 rounded-lg border border-stone-200 px-3 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                        <button
                          onClick={saveGoal}
                          disabled={saving}
                          aria-label="Save intention"
                          className="text-green-600 transition-colors hover:text-green-700 disabled:opacity-50"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingGoal(false);
                            setGoalError(null);
                          }}
                          aria-label="Cancel"
                          className="text-stone-400 transition-colors hover:text-stone-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {goalError && <p className="mt-2 text-xs text-destructive">{goalError}</p>}
                    </div>
                  ) : (
                    <>
                      <p className="font-display text-lg text-foreground">
                        {user?.journalingGoal || <span className="text-muted-foreground">Not set</span>}
                      </p>
                      {!user?.journalingGoal && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          One sentence about why you write. It shows up at the top of every new unfolding.
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className="border-t border-foreground/5" />

                <div className="flex w-full cursor-not-allowed items-center gap-3 px-6 py-4 text-left opacity-60">
                  <Bell className="h-4 w-4 shrink-0 text-stone-400" />
                  <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Reminders</span>
                  <span className="shrink-0 text-xs text-muted-foreground">Coming soon</span>
                </div>
              </Card>
            </section>

            {/* ── Calendar, then the AI assistants that schedule through it ── */}
            <CalendarFlowProvider origin="settings">
              <CalendarSettingsSection />
            </CalendarFlowProvider>
            <AiAssistantsSection />

            {/* ── Plan & usage ────────────────────────────────────────── */}
            <section>
              <SectionHeading>Plan &amp; usage</SectionHeading>
              <Card className="p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <h3 className="font-display text-2xl font-medium tracking-[-0.015em] text-foreground">{currentPlan.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {isSubscriber ? unfoldingsLabel(currentPlan) : `${limit} unfoldings included`}
                  </p>
                </div>

                <div className="mt-5 border-t border-foreground/5 pt-5">
                  {hasLimit && (
                    <>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="font-semibold text-foreground">{used} used</span>
                        {remaining != null && <span className="text-muted-foreground">{remaining} left</span>}
                      </div>
                      <div
                        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-foreground/[0.08]"
                        role="progressbar"
                        aria-valuenow={used}
                        aria-valuemin={0}
                        aria-valuemax={limit}
                        aria-label="Unfoldings used this month"
                      >
                        <div
                          className="h-full rounded-full bg-primary/60 transition-all"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </>
                  )}

                  <p className="mt-3 text-sm text-muted-foreground">
                    {planCancelsAt
                      ? `Cancels on ${planCancelsAt} — full access until then`
                      : exhausted
                        ? isSubscriber
                          ? `You've used all ${limit} unfoldings this month — more on the 1st`
                          : "You've hit the Starter limit — upgrade to continue"
                        : hasLimit
                          ? `Resets ${resetsOn}${paceLabel ? ` · ${paceLabel}` : ""}`
                          : "You're all set — keep unfolding."}
                  </p>

                  {!planCancelsAt && pendingPlanName && pendingEffectiveFriendly && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Switches to {pendingPlanName} on {pendingEffectiveFriendly}.{" "}
                      <button
                        type="button"
                        onClick={handleUndoSwitch}
                        disabled={undoing}
                        className="underline underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50"
                      >
                        {undoing ? "Undoing…" : "Undo"}
                      </button>
                    </p>
                  )}
                  {undoError && <p className="mt-2 text-xs text-destructive">{undoError}</p>}
                  {paymentFailing && (
                    <p role="alert" className="mt-3 rounded-lg border border-amber-200/70 bg-amber-50/70 px-3 py-2 text-xs text-amber-800">
                      Your last renewal payment didn&apos;t go through. You still have full access while we retry —
                      update your card under Manage billing to keep your plan.
                    </p>
                  )}
                </div>

                <div className="mt-5 flex flex-col gap-3">
                  <Button
                    onClick={() => setLocation("/pricing")}
                    className="h-10 w-full gap-1.5 rounded-full px-4 text-sm text-white"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    {isSubscriber ? "Manage plan" : "Upgrade"}
                  </Button>

                  {hasBillingPortal && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleManageBilling()}
                      disabled={openingPortal}
                      className="h-10 w-full gap-1.5 rounded-full px-4 text-sm disabled:opacity-60"
                    >
                      {openingPortal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                      Manage billing
                    </Button>
                  )}
                  {portalError && <p className="text-xs text-destructive">{portalError}</p>}

                  {/* Scheduled-switch route for existing Plus subscribers, so
                      Plus → Premium never starts a second subscription. */}
                  {canSwitchToPremium && (
                    <Button
                      variant="outline"
                      onClick={() => setSwitchTarget("premium")}
                      className="h-10 w-full rounded-full px-4 text-sm"
                    >
                      Upgrade to Premium
                    </Button>
                  )}

                  <div className="flex items-center justify-between gap-3 px-1">
                    <button
                      onClick={() => setLocation("/pricing")}
                      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Compare plans <ArrowRight className="h-3 w-3" />
                    </button>
                    {isSubscriber && !planCancelsAt && (
                      <button
                        type="button"
                        onClick={() => setCancelOpen(true)}
                        className="text-xs font-medium text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
                      >
                        Cancel plan
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            </section>

            {/* ── Account & security ──────────────────────────────────── */}
            <section>
              <SectionHeading>Account &amp; security</SectionHeading>
              <Card className="overflow-hidden">
                <button
                  onClick={handleChangePassword}
                  disabled={passwordState === "sending"}
                  className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-foreground/[0.02] disabled:opacity-60"
                >
                  <KeyRound className="h-4 w-4 shrink-0 text-stone-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">Reset password</span>
                    {passwordState === "sent" && (
                      <span className="mt-0.5 flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle2 className="h-3 w-3" /> Check your inbox for a link to change your password.
                      </span>
                    )}
                    {passwordState === "no_password" && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        We've sent a link to set a password (you currently sign in with Google).
                      </span>
                    )}
                    {passwordState === "error" && passwordError && (
                      <span className="mt-0.5 block text-xs text-destructive">{passwordError}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {passwordState === "sending" ? "Sending…" : ""}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />
                </button>

                <div className="border-t border-foreground/5" />

                <Link
                  href="/contact"
                  className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-foreground/[0.02]"
                >
                  <Mail className="h-4 w-4 shrink-0 text-stone-400" />
                  <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Contact us</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />
                </Link>

                <div className="border-t border-foreground/5" />

                <button
                  onClick={logout}
                  className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-foreground/[0.02]"
                >
                  <LogOut className="h-4 w-4 shrink-0 text-stone-400" />
                  <span className="min-w-0 flex-1 text-sm font-medium text-foreground">Sign out</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />
                </button>
              </Card>
            </section>

            {/* ── Deactivate ──────────────────────────────────────────── */}
            <section>
              <SectionHeading>Deactivate account</SectionHeading>
              <Card className="overflow-hidden">
                <button
                  onClick={() => setDeactivateOpen(true)}
                  className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-destructive/5"
                >
                  <UserMinus className="h-4 w-4 shrink-0 text-destructive" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-destructive">Deactivate account</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                      Signs you out everywhere and blocks access. Your unfoldings and account data are kept —
                      signing in again restores your account.
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-stone-300" />
                </button>
              </Card>
            </section>

            {/* ── Privacy note ────────────────────────────────────────── */}
            <div className="flex items-start gap-3 px-1 pb-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Lock className="h-3.5 w-3.5 text-primary" />
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Your unfoldings are private and secure. We never use your writing to train AI models.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <CancelPlanDialog open={cancelOpen} onOpenChange={setCancelOpen} />
      <DeleteAccountDialog open={deactivateOpen} onOpenChange={setDeactivateOpen} />
      <SwitchPlanDialog
        targetPlanId={switchTarget}
        onOpenChange={(open) => {
          if (!open) setSwitchTarget(null);
        }}
      />
    </Layout>
  );
}
